/**
 * UpdateDocumentDatabaseModal Component
 *
 * Modal for updating a document database (name, description, schema version).
 */

import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Modal } from '../../../components/ui/Modal';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Textarea } from '../../../components/ui/Textarea';
import { Alert } from '../../../components/ui/Alert';
import { FormField } from '../../../components/forms/FormField';
import { CopyableCode } from '../../../components/ui/CopyableCode';
import { useUpdateDocumentDatabase } from '../hooks/useUpdateDocumentDatabase';
import { updateDocumentDBFormSchema, type UpdateDocumentDBFormData } from '../../../lib/validators/document-database';
import type { DocumentDatabaseSanitized } from '../../../types/document-database.types';

interface UpdateDocumentDatabaseModalProps {
  isOpen: boolean;
  onClose: () => void;
  tenantId: string;
  documentDB: DocumentDatabaseSanitized;
}

/**
 * UpdateDocumentDatabaseModal component
 */
export const UpdateDocumentDatabaseModal: React.FC<UpdateDocumentDatabaseModalProps> = ({
  isOpen,
  onClose,
  tenantId,
  documentDB,
}) => {
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [successMessage, setSuccessMessage] = useState<string>('');

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<UpdateDocumentDBFormData>({
    resolver: zodResolver(updateDocumentDBFormSchema),
    defaultValues: {
      documentDBName: documentDB.documentDBName,
      description: documentDB.description || '',
      schemaVersion: documentDB.schemaVersion || '',
    },
  });

  // Reset form when document database changes
  useEffect(() => {
    if (isOpen) {
      reset({
        documentDBName: documentDB.documentDBName,
        description: documentDB.description || '',
        schemaVersion: documentDB.schemaVersion || '',
      });
    }
  }, [isOpen, documentDB, reset]);

  const { mutate: updateDocumentDatabase, isPending } = useUpdateDocumentDatabase(
    tenantId,
    documentDB.documentDBId,
    {
      onSuccess: () => {
        setSuccessMessage('Document database updated successfully!');
        setErrorMessage('');
        setTimeout(() => {
          reset();
          onClose();
          setSuccessMessage('');
        }, 1500);
      },
      onError: (error) => {
        setErrorMessage(error);
        setSuccessMessage('');
      },
    }
  );

  const onSubmit = (data: UpdateDocumentDBFormData) => {
    setErrorMessage('');
    setSuccessMessage('');

    // Build update DTO (only include non-empty fields)
    const updateData: { documentDBName?: string; description?: string | null; schemaVersion?: string | null } = {};

    if (data.documentDBName && data.documentDBName !== documentDB.documentDBName) {
      updateData.documentDBName = data.documentDBName;
    }
    if (data.description !== undefined && data.description !== (documentDB.description || '')) {
      updateData.description = data.description || null;
    }
    if (data.schemaVersion !== undefined && data.schemaVersion !== (documentDB.schemaVersion || '')) {
      updateData.schemaVersion = data.schemaVersion || null;
    }

    // Must have at least one field to update
    if (Object.keys(updateData).length === 0) {
      setErrorMessage('No changes to update');
      return;
    }

    updateDocumentDatabase(updateData);
  };

  const handleClose = () => {
    if (!isPending) {
      reset();
      setErrorMessage('');
      setSuccessMessage('');
      onClose();
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="lg">
      <Modal.Header>Update Document Database</Modal.Header>
      <Modal.Body>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {/* DocumentDB Code (read-only) */}
          <div className="bg-gray-50 p-3 rounded-md">
            <p className="text-sm text-gray-600">
              Database Code:{' '}
              <CopyableCode value={documentDB.documentDBCode} />
            </p>
            <p className="mt-1 text-xs text-gray-500">
              Code is immutable and cannot be changed.
            </p>
          </div>

          {/* Error Message */}
          {errorMessage && <Alert variant="error">{errorMessage}</Alert>}

          {/* Success Message */}
          {successMessage && <Alert variant="success">{successMessage}</Alert>}

          {/* DocumentDB Name */}
          <FormField label="Database Name" required error={errors.documentDBName?.message}>
            <Input
              {...register('documentDBName')}
              placeholder="e.g., User Profiles"
              error={!!errors.documentDBName}
            />
          </FormField>

          {/* Description */}
          <FormField label="Description" error={errors.description?.message}>
            <Textarea
              {...register('description')}
              placeholder="Describe the purpose of this document database..."
              rows={3}
              error={!!errors.description}
            />
          </FormField>

          {/* Schema Version */}
          <FormField label="Schema Version" error={errors.schemaVersion?.message}>
            <Input
              {...register('schemaVersion')}
              placeholder="e.g., 1.0.0"
              error={!!errors.schemaVersion}
            />
            <p className="mt-1 text-xs text-gray-500">
              Optional version identifier for your document schema.
            </p>
          </FormField>

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 pt-4">
            <Button type="button" variant="secondary" onClick={handleClose} disabled={isPending}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" loading={isPending}>
              Update Database
            </Button>
          </div>
        </form>
      </Modal.Body>
    </Modal>
  );
};
